import { useCallback, useRef, useState } from "react";

/**
 * Custom hook for copying text to the clipboard with a transient "copied" flag.
 * @param {number} [resetDelayMs] - How long the `copied` flag stays true, in milliseconds. Defaults to 1000.
 * @returns An object with the `copied` flag and a `copy` function.
 * @example
 * const { copied, copy } = useCopyToClipboard();
 * <button onClick={() => copy(email)}>{copied ? "Copied" : "Copy"}</button>
 */
export const useCopyToClipboard = (resetDelayMs: number = 1000) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const copy = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setCopied(false), resetDelayMs);
    },
    [resetDelayMs],
  );

  return { copied, copy };
};
